import { addTo, getOrCreate } from '../../../util/index.js';
import { payableUnder } from './ceiling.js';
import { claimableOpen, claimOf } from './claims.js';
import { settleDerived } from './derived.js';
import { consume } from './ledger.js';
// v1's allocation order relative to the voucher being appended: same category
// first (newest first), then category tier, date and id (D13).
export const compareAllocation = (a, b, appended) => {
    const category = appended.category;
    const aSame = a.category === category;
    const bSame = b.category === category;
    if (aSame && bSame) {
        const byDate = b.instant - a.instant;
        if (byDate !== 0) {
            return byDate;
        }
    }
    else if (aSame) {
        return -1;
    }
    else if (bSame) {
        return 1;
    }
    return (a.tier - b.tier ||
        a.instant - b.instant ||
        a.sequence - b.sequence ||
        a.inputIndex - b.inputIndex);
};
export const balance = (entry) => {
    settleDerived(entry);
    const raw = entry.ledger !== null
        ? claimableOpen(entry.ledger, claimOf(entry))
        : entry.distributed - entry.inputAmount;
    const adjusted = raw + entry.reservation;
    if (raw > 0) {
        return Math.max(0, adjusted);
    }
    if (raw < 0) {
        return Math.min(0, adjusted);
    }
    return 0;
};
// Allocations to debt and interest are negative, of money positive.
const signOf = (entry) => entry.kind === 'credit' || entry.kind === 'none' ? 1 : -1;
export const addDistribution = (entry, counterpart, amount, date, fixedDistributionId) => {
    const signed = signOf(entry) * amount;
    entry.distributed += signed;
    entry.voucher.distributionAmount = entry.distributed;
    entry.voucher.distribution.push({
        caseVoucherId: counterpart,
        amount: signed,
        date,
        fixedDistributionId,
    });
};
// Books `amount` between the two, each side dated as given.
export const distributeBetween = (source, target, amount, sourceDate, targetDate, fixedDistributionId) => {
    addDistribution(source, target.caseVoucherId, amount, sourceDate, fixedDistributionId);
    addDistribution(target, source.caseVoucherId, amount, targetDate, fixedDistributionId);
};
// Pays `amount` of a target's open debt, oldest first, of covered interest
// only what can be claimed; returns what was paid.
export const payTarget = (target, amount) => {
    if (target.ledger === null) {
        return amount;
    }
    settleDerived(target);
    const claim = claimOf(target);
    if (claim === undefined) {
        return consume(target.ledger, amount);
    }
    target.caseState.eventClaims = undefined;
    return consume(target.ledger, amount, payableUnder(claim));
};
export const noteJointConsumption = (pending, source, target, paid) => {
    if (paid === 0 || target.associationId == null) {
        return;
    }
    addTo(getOrCreate(pending, source, () => new Map()), target, paid);
};
export const noteJointTargets = (pending, source, targets) => {
    for (const [target, paid] of targets) {
        noteJointConsumption(pending, source, target, paid);
    }
};
const allocationCandidates = (caseState, appended, own) => {
    const candidates = [];
    for (const tier of caseState.appendedByTier) {
        for (const entry of tier) {
            const other = entry === appended ? 0 : balance(entry);
            if ((own > 0 && other < 0) || (own < 0 && other > 0)) {
                candidates.push(entry);
            }
        }
    }
    return candidates.toSorted((a, b) => compareAllocation(a, b, appended));
};
// A manual distribution pays at most what both sides hold at its date (D12).
const applyFixed = (source, target, amount, date, fixedDistributionId) => {
    const limit = Math.min(amount, Math.abs(balance(source)), Math.abs(balance(target)));
    if (limit <= 0) {
        return 0;
    }
    const paid = payTarget(target, limit);
    if (paid === 0) {
        return 0;
    }
    if (source.ledger !== null) {
        consume(source.ledger, paid);
    }
    distributeBetween(source, target, paid, date, date, fixedDistributionId);
    return paid;
};
// Pays the targets in allocation order with what the appended voucher holds.
export const allocateDynamic = (run, caseState, entry, jointPaid) => {
    // Pays what it can of `target`; returns what was paid.
    const pay = (target) => {
        const own = balance(entry);
        const other = balance(target);
        if (own === 0 || other === 0 || Math.sign(other) === Math.sign(own)) {
            return 0;
        }
        const amount = Math.min(Math.abs(own), Math.abs(other));
        const debt = own > 0 ? entry : target;
        const credit = own > 0 ? target : entry;
        const paid = payTarget(debt, amount);
        if (paid === 0) {
            return 0;
        }
        if (credit.ledger !== null) {
            consume(credit.ledger, paid);
        }
        distributeBetween(entry, target, paid, target.date, entry.date, undefined);
        if (run.mode === 'derive' && credit.jointSource) {
            noteJointConsumption(jointPaid, credit, debt, paid);
        }
        return paid;
    };
    const covered = caseState.interestEntries
        .filter((other) => other.appended && other.ceilingCovered)
        .toSorted((a, b) => compareAllocation(a, b, entry));
    for (const target of allocationCandidates(caseState, entry, balance(entry))) {
        if (balance(entry) === 0) {
            return;
        }
        if (pay(target) === 0 || !target.ceilingCovered) {
            continue;
        }
        // Paying a covered target moves what the ceiling lets the others claim
        // (D31): on the day it runs out, the rounding øre of the shares can move
        // to a covered target already passed, which is paid before going on.
        const passed = covered.filter((other) => other !== target && compareAllocation(other, target, entry) < 0);
        let moved = true;
        while (moved && balance(entry) !== 0) {
            moved = passed.some((other) => pay(other) > 0);
        }
    }
};
// The appended voucher's manual and pinned distributions, in that order.
export const applyDistributions = (run, entry, fixedJoint) => {
    const { caseState } = entry;
    const id = entry.caseVoucherId;
    for (const fd of caseState.manualBySource.get(id) ?? []) {
        const target = caseState.byId.get(fd.toCaseVoucherId);
        entry.reservation -= fd.amount;
        target.reservation += fd.amount;
        const paid = applyFixed(entry, target, fd.amount, entry.date, fd.id);
        if (run.mode === 'derive' && entry.jointSource) {
            noteJointConsumption(fixedJoint, entry, target, paid);
        }
    }
    for (const fd of caseState.pinnedBySource.get(id) ?? []) {
        const target = caseState.byId.get(fd.toCaseVoucherId);
        applyFixed(entry, target, fd.amount, entry.date, fd.id);
    }
};
// What the appended voucher holds for the debts it refers to (see
// prepareEarmarks), paid now.
export const applyEarmarks = (run, entry, fixedJoint) => {
    const id = entry.caseVoucherId;
    for (const { target, amount } of entry.caseState.earmarksBySource.get(id) ??
        []) {
        entry.reservation -= amount;
        target.reservation += amount;
        const payable = Math.min(amount, Math.max(0, balance(target)), Math.max(0, -balance(entry)));
        if (payable <= 0) {
            continue;
        }
        const paid = payTarget(target, payable);
        distributeBetween(entry, target, paid, target.date, entry.date, undefined);
        if (run.mode === 'derive' && entry.jointSource) {
            noteJointConsumption(fixedJoint, entry, target, paid);
        }
    }
};
//# sourceMappingURL=allocation.js.map